/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import api from './api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Build the WebSocket URL from BACKEND_URL, respecting http->ws / https->wss.
 */
const buildWsUrl = (convId, token) => {
  if (!BACKEND_URL) return null;
  let base = BACKEND_URL;
  if (base.startsWith('https://')) base = 'wss://' + base.slice('https://'.length);
  else if (base.startsWith('http://')) base = 'ws://' + base.slice('http://'.length);
  return `${base}/api/ws/chat/${encodeURIComponent(convId)}?token=${encodeURIComponent(token)}`;
};

/**
 * Fetch a short-lived token from the backend for WebSocket authentication.
 * The cookie-based session is sent automatically by the browser (withCredentials).
 */
const fetchWsToken = async () => {
  // Short-lived WebSocket token. Session cookie (httpOnly) is sent automatically.
  // Sprint 9I: legacy localStorage fallback removed for security.
  try {
    const res = await api.get('/auth/ws-token');
    return res.data?.token || null;
  } catch (e) {
    return null;
  }
};

/**
 * useChatSocket
 * Establishes a WebSocket for a given conversation, with reconnect + keepalive.
 *
 * @param {string|null} conversationId
 * @param {(event: {type: string, data?: any, message_id?: string}) => void} onEvent
 * @returns {{ connected: boolean, sendPing: () => void }}
 */
export function useChatSocket(conversationId, onEvent) {
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectRef = useRef(null);
  const pingRef = useRef(null);
  const closedByUnmountRef = useRef(false);
  const handlerRef = useRef(onEvent);

  useEffect(() => { handlerRef.current = onEvent; }, [onEvent]);

  const cleanup = useCallback(() => {
    if (pingRef.current) { clearInterval(pingRef.current); pingRef.current = null; }
    if (reconnectRef.current) { clearTimeout(reconnectRef.current); reconnectRef.current = null; }
    if (wsRef.current) {
      try { wsRef.current.close(); } catch {}
      wsRef.current = null;
    }
    setConnected(false);
  }, []);

  useEffect(() => {
    if (!conversationId) return;
    closedByUnmountRef.current = false;

    let cancelled = false;
    const connect = async () => {
      // Fetch a fresh short-lived WS token via httpOnly cookie session.
      const token = await fetchWsToken();
      if (cancelled) return;
      const url = buildWsUrl(conversationId, token);
      if (!url || !token) return;
      let ws;
      try { ws = new WebSocket(url); } catch { return; }
      wsRef.current = ws;

      ws.onopen = () => {
        setConnected(true);
        // Keepalive ping every 25s
        pingRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            try { ws.send('ping'); } catch {}
          }
        }, 25000);
      };
      ws.onmessage = (evt) => {
        // 'pong' or JSON
        if (evt.data === 'pong') return;
        try {
          const payload = JSON.parse(evt.data);
          if (handlerRef.current) handlerRef.current(payload);
        } catch { /* ignore non-JSON */ }
      };
      ws.onerror = () => { /* handled by onclose */ };
      ws.onclose = () => {
        setConnected(false);
        if (pingRef.current) { clearInterval(pingRef.current); pingRef.current = null; }
        // Auto-reconnect with backoff (~3s)
        if (!closedByUnmountRef.current) {
          reconnectRef.current = setTimeout(connect, 3000);
        }
      };
    };

    connect();
    return () => {
      cancelled = true;
      closedByUnmountRef.current = true;
      cleanup();
    };
  }, [conversationId, cleanup]);

  const sendPing = useCallback(() => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      try { ws.send('ping'); } catch {}
    }
  }, []);

  return { connected, sendPing };
}

export default useChatSocket;
